import type { DesktopPickedAttachment } from "@afar-research/contracts";

import { desktopComposerAttachment, type ComposerPathAttachment } from "./composerAttachments";
import {
  type ComposerEnvironmentMachine,
  type ComposerFilesAndFoldersOpenStrategy,
  resolveComposerFilesAndFoldersOpenStrategy,
} from "./composerFilesAndFoldersStrategy.logic";
import { pickHostNativeComposerAttachments } from "./hostNativeAttachments";

/** Resolve the picker for the current supported client surface and the thread's Environment. */
export function resolveRuntimeComposerFilesAndFoldersOpenStrategy(
  environmentMachine: ComposerEnvironmentMachine,
): ComposerFilesAndFoldersOpenStrategy {
  return resolveComposerFilesAndFoldersOpenStrategy({
    hasDesktopPickAttachments:
      typeof window !== "undefined" && typeof window.desktopBridge?.pickAttachments === "function",
    environmentMachine,
  });
}

export type ComposerFilesAndFoldersOpenResult =
  | {
      readonly kind: "desktop-mixed";
      readonly attachments: ReadonlyArray<DesktopPickedAttachment>;
    }
  | {
      readonly kind: "host-native-mixed";
      readonly attachments: ReadonlyArray<ComposerPathAttachment>;
    }
  | { readonly kind: "browser-file-input" }
  | { readonly kind: "failed" };

/**
 * Opens the primary Files and folders picker.
 */
export async function openComposerFilesAndFolders(input: {
  readonly pickDesktopAttachments?: () => Promise<
    ReadonlyArray<DesktopPickedAttachment> | undefined
  >;
  readonly pickHostNativeAttachments?: () => Promise<ReadonlyArray<ComposerPathAttachment> | null>;
  readonly openBrowserFileInput: () => void;
}): Promise<ComposerFilesAndFoldersOpenResult> {
  if (input.pickDesktopAttachments) {
    const attachments = (await input.pickDesktopAttachments()) ?? [];
    return { kind: "desktop-mixed", attachments };
  }
  if (input.pickHostNativeAttachments) {
    // Async on purpose: yields so the UI stays responsive while NSOpenPanel runs.
    const attachments = await input.pickHostNativeAttachments();
    if (attachments === null) {
      return { kind: "failed" };
    }
    return { kind: "host-native-mixed", attachments };
  }
  // Sync Chromium open — only when no native mixed picker is available.
  input.openBrowserFileInput();
  return { kind: "browser-file-input" };
}

export type RuntimeComposerFilesAndFoldersOpenResult =
  | {
      readonly kind: "attachments";
      readonly attachments: ReadonlyArray<ComposerPathAttachment>;
    }
  | { readonly kind: "browser-file-input" }
  | { readonly kind: "failed" };

/**
 * Open the files-and-folders picker for the current supported client surface
 * and normalize every native result into composer attachments.
 */
export async function openRuntimeComposerFilesAndFolders(input: {
  readonly makeId: () => string;
  readonly openBrowserFileInput: () => void;
  readonly strategy: ComposerFilesAndFoldersOpenStrategy;
}): Promise<RuntimeComposerFilesAndFoldersOpenResult> {
  const strategy = input.strategy;
  const result = await openComposerFilesAndFolders({
    ...(strategy.kind === "desktop-mixed"
      ? {
          pickDesktopAttachments: async () => window.desktopBridge?.pickAttachments?.(),
        }
      : {}),
    ...(strategy.kind === "host-native-mixed"
      ? {
          pickHostNativeAttachments: () => pickHostNativeComposerAttachments(input.makeId),
        }
      : {}),
    openBrowserFileInput: input.openBrowserFileInput,
  });
  if (result.kind === "desktop-mixed") {
    return {
      kind: "attachments",
      attachments: result.attachments.map((attachment) =>
        desktopComposerAttachment(input.makeId(), attachment),
      ),
    };
  }
  if (result.kind === "host-native-mixed") {
    return { kind: "attachments", attachments: result.attachments };
  }
  return result;
}
